import { useEffect, useLayoutEffect, useRef } from 'react'

const PUBSUB_WS_URL = (import.meta.env.VITE_PUBSUB_URL as string).replace('https://', 'wss://')

const MIN_DELAY_MS = 1_000
const MAX_DELAY_MS = 30_000

interface Props<T> {
  endpoint: string
  skip?: boolean
  onData: (data: T) => void
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useSub = <T = Record<string, any>>({
  endpoint,
  skip,
  onData,
}: Props<T>) => {
  const memoizedFunction = useRef(onData)

  useLayoutEffect(() => {
    memoizedFunction.current = onData
  }, [onData])

  useEffect(() => {
    if (skip) return

    let ws: WebSocket
    let retryTimer: ReturnType<typeof setTimeout>
    let delay = MIN_DELAY_MS
    let destroyed = false

    function connect() {
      ws = new WebSocket(`${PUBSUB_WS_URL}${endpoint}`)

      ws.onmessage = ({ data }) => {
        try {
          const parsed = JSON.parse(data as string)
          memoizedFunction.current(parsed.data ?? parsed)
        } catch {}
        delay = MIN_DELAY_MS
      }

      ws.onerror = () => {}

      ws.onclose = () => {
        if (destroyed) return
        retryTimer = setTimeout(() => {
          if (!destroyed) connect()
        }, delay)
        delay = Math.min(delay * 2, MAX_DELAY_MS)
      }
    }

    connect()

    return () => {
      destroyed = true
      clearTimeout(retryTimer)
      ws?.close()
    }
  }, [endpoint, skip])
}
